import { LitElement, property } from 'lit-element'
import { contextualize } from '@polleverywhere/cutlass'
import { register } from '@polleverywhere/hmr-custom-element'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import './breadcrumb-item'
import './breadcrumb.scss'

/**
 * Renders a breadcrumb trail to help users navigate up and down a directory
 * tree or hiearchy. Each breadcrumb item can behave as a standard anchor
 * element link that redirects users to a specified path, or it can emit an
 * event for consumers to listen and react to.
 *
 * ```
 * // Standard setup using default config
 * <pe-breadcrumb>
 *   <pe-breadcrumb-item>Root</pe-breadcrumb-item>
 *   <pe-breadcrumb-item>First</pe-breadcrumb-item>
 *   <pe-breadcrumb-item>Second</pe-breadcrumb-item>
 *   <pe-breadcrumb-item>Third</pe-breadcrumb-item>
 *   <pe-breadcrumb-item>Leaf</pe-breadcrumb-item>
 * </pe-breadcrumb>
 * ```
 *
 * There is a default level threshold that determines when a breadcrumb should
 * consolidate levels into a collapsed menu. These are configurable using the
 * following options.
 *
 * ```
 * // Custom thresholds for collapsing items into a menu
 * <pe-breadcrumb max-items=4 items-before-collapse=2 items-after-collapse=1>
 *   ...
 * </pe-breadcrumb>
 * ```
 *
 * Custom separators can replace the existing `/` character.
 *
 * ```
 * // Custom separator
 * <pe-breadcrumb separator='>>'>
 *   ...
 * </pe-breadcrumb>
 * ```
 *
 * @element pe-breadcrumb
 *
 * @slot - Content child nodes containing the list of breadcrumbs
 *
 * @prop {number} itemsAfterCollapse=Infinity - If max items is exceeded, the number
 *  of breadcrumbs to show after the ellipsis.
 * @prop {number} itemsBeforeCollapse=1 - If max items is exceeded, the number
 *  of breadcrumbs to show before the ellipsis.
 * @prop {number} maxItems=4 - Specifies the maximum number of breadcrumbs to
 *  display. When there are more than the maximum number, only the first
 *  itemsBeforeCollapse and last itemsAfterCollapse will be shown, with a
 *  clickable ellipsis in between that toggles a menu containing the hidden
 *  breadcrumbs.
 * @prop {boolean} mobile=false - Renders breadcrumbs in a smaller form-factor
 *  optimized for mobile viewports.
 * @prop {string} separator=/ - Render this separator between breadcrumbs.
 *
 * @cssprop --pe-breadcrumb-item-color=$pe-neutral-600 - Unlinked item color
 * @cssprop --pe-breadcrumb-link-color=$pe-blue-500 - Linked item color
 * @cssprop --pe-breadcrumb-separator-color=$pe-neutral-400 - Separator color
 *
 * @fires {Object} action - Fires when breadcrumb is selected.
 */
export default class Breadcrumb extends LitElement {
  static get tagName () { return 'pe-breadcrumb' }

  @property({ type: Number, attribute: 'items-after-collapse' })
  itemsAfterCollapse = Infinity

  @property({ type: Number, attribute: 'items-before-collapse' })
  itemsBeforeCollapse = 1

  @property({ type: Number, attribute: 'max-items' })
  maxItems = 4

  @property({ type: Boolean, reflect: true })
  mobile = false

  @property({ type: String })
  separator = '/'

  constructor () {
    super()

    // contextualize cutlass events
    contextualize(this, this.tagName)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)

    // React to added/removed breadcrumb items by always re-rendering.
    if (!this._observer) {
      this._observer = new MutationObserver((list) => this.requestUpdate())
      this._observer.observe(this, { childList: true })
    }
  }

  createRenderRoot () { return this }

  render () {
    const items = Array.from(this.querySelectorAll('pe-breadcrumb-item'))
    items.forEach((item, index) => {
      // Normalize all items. We must do this whenever child items get added and
      // removed so they can get reset to their default states before attempting
      // to collapse them based on max item constraints.
      item.isFirst = index === 0
      item.isLast = index === items.length - 1
      item.collapsed = false
      item.separator = this.separator
      item.menu = []
    })

    if (items.length > this.maxItems) {
      // Normalize the before/after collapse counts with certain constraints.
      // There must always be at least one item before and after the collapsing
      // index. There can also never show more items than the max items count.
      const itemsBefore = Math.max(
        Math.min(this.itemsBeforeCollapse, this.maxItems - 1), 1)
      const itemsAfter = Math.max(
        Math.min(this.itemsAfterCollapse, this.maxItems - itemsBefore), 1)

      // Splice out the items that will flow into the collapsed menu.
      const spliceCount = items.length - itemsBefore - itemsAfter
      const collapsedItems = items.splice(itemsBefore, spliceCount)

      // Move all collapsed items into the previous sibling's menu.
      collapsedItems.forEach(item => { item.collapsed = true })
      items[itemsBefore - 1].menu = collapsedItems
    }

    return this.slots()
  }
}

register(module, Breadcrumb.tagName, Breadcrumb)
